/**
 * Реестр контуров: фильтры, культура строки и страницы таблицы. Чистые
 * функции без UI (`Contours.tsx`).
 */
import type { ContourMeta, QuarantineStatusValue } from '../data/types';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

/** Сентинел фильтра «размещение неизвестно»: настоящий `crop_2026` бывает `null`. */
export const NOT_PLACED = '__none__';

export const CONTOUR_PAGE_SIZE = 25;

/** `hot` — красные и жёлтые; пусто — без фильтра. */
export type QuarantineFilter = '' | 'hot' | 'red' | 'watch';

export interface ContourFilters {
  massif: string;
  crop: string;
  geometry: string;
  quarantine: QuarantineFilter;
}

type CropFeature = { properties?: Record<string, unknown> | null };

/**
 * Культура контура — та же, что красит карту: строка реестра, а если в ней
 * пусто (канон размещения строкой не отдаёт) — культура фичи карты на дату
 * среза. `null` — размещение не названо нигде.
 */
export function contourCropOf(features: ReadonlyArray<CropFeature>): (row: Pick<ContourMeta, 'id' | 'crop_2026'>) => string | null {
  const byId = new Map<string, string>();
  for (const f of features) {
    const id = f.properties?.id;
    const crop = f.properties?.crop_2026;
    if (id !== undefined && id !== null && typeof crop === 'string' && crop !== '') byId.set(String(id), crop);
  }
  return (row) => row.crop_2026 ?? byId.get(row.id) ?? null;
}

const CYCLE = defineMessages({
  noPlacement: { id: 'contour.registry.cycle.reason.noPlacement', defaultMessage: 'размещения нет', description: 'Причина в колонке «Агроцикл / культура» реестра контуров: у контура не названа культура; после «вне агроцикла:», строчными' },
  noCycle: { id: 'contour.registry.cycle.reason.noCycle', defaultMessage: 'агроцикла под эту культуру и сезон нет', description: 'Причина в колонке «Агроцикл / культура»: культура названа, но агроцикла под неё и сезон нет; строчными' },
  partsDiffer: { id: 'contour.registry.cycle.reason.partsDiffer', defaultMessage: 'части контура в разных агроциклах', description: 'Причина в колонке «Агроцикл / культура»: части контура относятся к разным агроциклам; строчными' },
  cropsOverlap: { id: 'contour.registry.cycle.reason.cropsOverlap', defaultMessage: 'на поле два агроцикла', description: 'Причина в колонке «Агроцикл / культура»: на поле одновременно два агроцикла, нужен выбор; строчными' },
  unknown: { id: 'contour.registry.cycle.reason.unknown', defaultMessage: 'агроцикл не определён', description: 'Причина в колонке «Агроцикл / культура», когда сервер причину не назвал; строчными' },
  untitled: { id: 'contour.registry.cycle.untitled', defaultMessage: 'агроцикл без названия', description: 'Ячейка колонки «Агроцикл / культура»: агроцикл определён, но названия у него нет' },
  ambiguous: { id: 'contour.registry.cycle.ambiguous', defaultMessage: 'несколько: {reason}', description: 'Ячейка колонки «Агроцикл / культура»: у контура несколько агроциклов; {reason} — причина словами' },
  outside: { id: 'contour.registry.cycle.outside', defaultMessage: 'вне агроцикла: {reason}', description: 'Ячейка колонки «Агроцикл / культура»: контур вне агроцикла; {reason} — причина словами' },
});

/** Причины «агроцикла нет» словами сервера (`work.agrocycle_lookup`). */
const CYCLE_REASON = {
  no_placement: CYCLE.noPlacement,
  no_cycle: CYCLE.noCycle,
  parts_differ: CYCLE.partsDiffer,
  crops_overlap: CYCLE.cropsOverlap,
} as const;

/**
 * Подпись колонки «Агроцикл / культура»: что назвал сервер, без догадок. `null` —
 * источник агроцикла не называет вовсе (фикстуры): колонка тогда не рисуется.
 */
export function contourCycleLabel(row: Pick<ContourMeta, 'cycle'>): { text: string; key: string | null } | null {
  const cycle = row.cycle;
  if (!cycle) return null;
  if (cycle.status === 'one') return { text: cycle.title ?? cycle.key ?? tr(CYCLE.untitled), key: cycle.key };
  const known = cycle.reason ? CYCLE_REASON[cycle.reason as keyof typeof CYCLE_REASON] : undefined;
  const reason = cycle.reason ? (known ? tr(known) : cycle.reason) : tr(CYCLE.unknown);
  return { text: cycle.status === 'ambiguous' ? tr(CYCLE.ambiguous, { reason }) : tr(CYCLE.outside, { reason }), key: null };
}

export function filterContours(
  rows: ReadonlyArray<ContourMeta>,
  filters: ContourFilters,
  cropOf: (row: ContourMeta) => string | null,
  quarantineById: Readonly<Record<string, QuarantineStatusValue>>,
): ContourMeta[] {
  return rows.filter((c) => {
    if (filters.massif && c.massif_id !== filters.massif) return false;
    if (filters.crop) {
      const crop = cropOf(c);
      if (filters.crop === NOT_PLACED ? crop !== null : crop !== filters.crop) return false;
    }
    if (filters.geometry && c.geometry !== filters.geometry) return false;
    if (filters.quarantine) {
      const q = quarantineById[c.id];
      if (filters.quarantine === 'hot' && q !== 'red' && q !== 'watch') return false;
      if (filters.quarantine === 'red' && q !== 'red') return false;
      if (filters.quarantine === 'watch' && q !== 'watch') return false;
    }
    return true;
  });
}

/** Строки страницы `page` (с нуля); страница за краем прижимается к последней. */
export function pageOf<T>(rows: ReadonlyArray<T>, page: number, size = CONTOUR_PAGE_SIZE): T[] {
  const last = Math.max(0, Math.ceil(rows.length / size) - 1);
  const p = Math.min(Math.max(0, page), last);
  return rows.slice(p * size, (p + 1) * size);
}
